import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, ListChecks, Pencil, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MoneyInput from "@/components/MoneyInput";
import { BRL } from "@/lib/api";

const blank = () => ({ description: "", quantity: 1, unit_value: 0 });
const num = (v) => Number(String(v).replace(",", ".")) || 0;
const sub = (r) => num(r.quantity) * (Number(r.unit_value) || 0);
const clean = (r) => ({ description: r.description.trim(), quantity: num(r.quantity), unit_value: Number(r.unit_value) || 0 });

export const materialListText = (items) =>
  items.map((r) => `${r.quantity}x ${r.description} — ${BRL(sub(r))}`).join("\n");

const Summary = ({ r, idx, active, edit, del }) => (
  <li className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 ${active ? "border-[#7c3aed] bg-violet-50" : "border-slate-200 bg-white"}`} data-testid={`mat-row-${idx}`}>
    <span className="text-xs font-black text-[#7c3aed] tabular-nums shrink-0">{num(r.quantity)}x</span>
    <span className="flex-1 min-w-0 text-sm font-semibold text-slate-800 truncate" data-testid={`mat-sum-desc-${idx}`}>{r.description}</span>
    <span className="text-sm font-black tabular-nums text-slate-900 shrink-0" data-testid={`mat-sub-${idx}`}>{BRL(sub(r))}</span>
    <button type="button" onClick={edit} className="p-1.5 text-slate-400 hover:text-violet-600 shrink-0" aria-label="Editar" data-testid={`mat-edit-${idx}`}><Pencil className="w-4 h-4" /></button>
    <button type="button" onClick={del} className="p-1.5 text-slate-400 hover:text-rose-600 shrink-0" aria-label="Remover" data-testid={`mat-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
  </li>
);

const Editor = ({ draft, setDraft, editing, onCommit, onCancelEdit, inputRef }) => (
  <form onSubmit={(e) => { e.preventDefault(); onCommit(); }} className="rounded-xl border-2 border-[#7c3aed]/40 bg-violet-50/50 p-2.5 space-y-2" data-testid="mat-editor">
    <p className="text-[11px] font-bold text-violet-700">{editing ? "Editando item" : "Novo item"}</p>
    <Input ref={inputRef} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="Descrição (ex.: Cimento 50kg)"
      className="h-10 bg-white" data-testid="mat-desc" />
    <div className="grid grid-cols-[64px_1fr_auto] gap-2 items-center">
      <Input type="number" min="0" step="any" inputMode="decimal" value={draft.quantity} onChange={(e) => setDraft({ ...draft, quantity: e.target.value })}
        className="h-10 bg-white" aria-label="Quantidade" data-testid="mat-qty" />
      <MoneyInput value={draft.unit_value} onChange={(v) => setDraft({ ...draft, unit_value: v })} placeholder="Valor unit." className="h-10 bg-white" data-testid="mat-unit" />
      <span className="text-sm font-black tabular-nums text-slate-800 min-w-[72px] text-right" data-testid="mat-draft-sub">{BRL(sub(draft))}</span>
    </div>
    <div className="flex gap-2">
      {editing && <Button type="button" variant="outline" onClick={onCancelEdit} className="h-10 bg-white" data-testid="mat-cancel-edit"><X className="w-4 h-4" /></Button>}
      <Button type="submit" disabled={!draft.description.trim()} className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="mat-add-row">
        {editing ? <><Check className="w-4 h-4 mr-1" /> Atualizar item</> : <><Plus className="w-4 h-4 mr-1" /> Adicionar item</>}
      </Button>
    </div>
  </form>
);

export default function MaterialListDialog({ open, onOpenChange, initial, onSave }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(blank());
  const [editIdx, setEditIdx] = useState(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (open) { setItems((initial || []).map((r) => ({ ...r }))); setDraft(blank()); setEditIdx(null); setTimeout(() => inputRef.current?.focus(), 150); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const focus = () => setTimeout(() => inputRef.current?.focus(), 30);
  const commit = () => {
    if (!draft.description.trim()) return;
    if (editIdx === null) {
      setItems((l) => [...l, clean(draft)]);
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" }), 30);
    } else setItems((l) => l.map((x, k) => (k === editIdx ? clean(draft) : x)));
    setDraft(blank()); setEditIdx(null); focus();
  };
  const edit = (i) => { setEditIdx(i); setDraft({ ...items[i] }); focus(); };
  const del = (i) => { setItems((l) => l.filter((_, k) => k !== i)); if (editIdx === i) { setEditIdx(null); setDraft(blank()); } };
  const final = draft.description.trim() && editIdx === null ? [...items, clean(draft)] : items;
  const total = final.reduce((s, r) => s + sub(r), 0);
  const save = () => { onSave(final, Math.round(total * 100) / 100); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] flex flex-col gap-3 w-[calc(100vw-1.5rem)] sm:w-full [&>*]:min-w-0" data-testid="material-list-dialog">
        <DialogHeader className="text-left shrink-0">
          <DialogTitle className="text-base text-[#1e3a8a] flex items-center gap-2"><ListChecks className="w-5 h-5" /> Lista de materiais</DialogTitle>
          <p className="text-xs text-slate-500">Os itens salvos ficam em cima. Digite o próximo embaixo.</p>
        </DialogHeader>
        <ul ref={listRef} className="space-y-2 overflow-y-auto min-h-0 flex-1 max-h-[38vh]" data-testid="mat-list">
          {items.length === 0 && <li className="text-xs text-slate-400 italic text-center py-3">Nenhum item ainda</li>}
          {items.map((r, i) => <Summary key={i} r={r} idx={i} active={editIdx === i} edit={() => edit(i)} del={() => del(i)} />)}
        </ul>
        <div className="shrink-0 space-y-3">
          <Editor draft={draft} setDraft={setDraft} editing={editIdx !== null} onCommit={commit} onCancelEdit={() => { setEditIdx(null); setDraft(blank()); }} inputRef={inputRef} />
          <div className="flex items-center justify-between rounded-xl bg-[#1e3a8a] text-white px-4 py-3">
            <span className="text-sm font-semibold">{final.length} {final.length === 1 ? "item" : "itens"} · Total</span>
            <span className="text-lg font-black tabular-nums" data-testid="mat-total">{BRL(total)}</span>
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1" data-testid="mat-cancel">Cancelar</Button>
            <Button type="button" onClick={save} disabled={!final.length || total <= 0} className="flex-1 bg-[#7c3aed] hover:bg-[#6d28d9] text-white" data-testid="mat-save">Salvar lista</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
